import { Cell, Grid, HeadingSection, Tabs, VFlow } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import useSession from 'components/auth/useSession'
import { RichTextField } from 'components/form/final-form/RichTextField'
import { TabLink } from 'components/route'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import React from 'react'
import { useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { MetaPath } from 'util/metaPath/metaPath'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { AtestadoPlanoModel } from './atestados/AtestadosView'
import { CiapPlanoField, CiapPlanoModel } from './components/CiapPlanoField'
import EvolucoesOdonto from './components/evolucoes-odontologicas/EvolucoesOdonto'
import { OdontogramaField, OdontogramaFieldModel } from './components/odontograma-field/OdontogramaField'
import { ProcedimentoPlanoField, ProcedimentoPlanoModel } from './components/ProcedimentoPlanoField'
import PlanoRootView from './PlanoRootView'
import { PrescricaoMedicamentoPlanoModel } from './prescricao-medicamento/PrescricaoMedicamentoView'

export interface PlanoFormModel {
  texto?: string
  ciaps?: CiapPlanoModel[]
  procedimentos?: ProcedimentoPlanoModel[]
  atestado?: AtestadoPlanoModel
  prescricaoMedicamento?: PrescricaoMedicamentoPlanoModel
  odontograma?: OdontogramaFieldModel
}

export interface PlanoFormProps {
  name: MetaPath<PlanoFormModel>
  cidadao: CidadaoAtendimento
  prontuarioId: ID
  tipoAtendProf: TipoAtendimentoProfissional
  isAtendimentoOdonto?: boolean
  atendimentoId: ID
  dataAtendimento: Instant
}

export const PlanoForm = (props: PlanoFormProps) => {
  const match = useRouteMatch()
  const { name, cidadao, prontuarioId, isAtendimentoOdonto, atendimentoId, dataAtendimento } = props

  const { hasAuthorization } = useSession({ fetchPolicy: 'cache-only' })

  const hasAutorizationDeclaracaoTab =
    hasAuthorization(Permissions.visualizarListaDeAtendimento.gerarDeclaracaoDeComparecimento) &&
    !hasAuthorization(Permissions.visualizarProntuario.soap.plano.visualizarAtestados)

  return (
    <>
      {!isAtendimentoOdonto && (
        <RichTextField
          name={name.texto}
          maxLength={4000}
          placeholder='Insira informações adicionais sobre o plano de cuidado.'
        />
      )}

      <VFlow>
        <HeadingSection level={3} title='Intervenções e/ou procedimentos clínicos realizados' vSpace={0}>
          <Grid>
            {!isAtendimentoOdonto && (
              <Cell size={12}>
                <CiapPlanoField name={name.ciaps} cidadao={cidadao} />
              </Cell>
            )}
            <Cell size={12}>
              <ProcedimentoPlanoField
                name={name.procedimentos}
                cidadao={cidadao}
                filtrarProcedimentosOdonto={isAtendimentoOdonto}
              />
            </Cell>
          </Grid>
        </HeadingSection>

        {isAtendimentoOdonto && (
          <VFlow>
            <HeadingSection level={3} title='Evoluções odontológicas'>
              <OdontogramaField
                name={name.odontograma}
                prontuarioId={prontuarioId}
                cidadaoDataNascimento={cidadao.dataNascimento}
              />
            </HeadingSection>

            <EvolucoesOdonto prontuarioId={prontuarioId} />
          </VFlow>
        )}

        <Grid>
          <Cell size={12}>
            <Tabs>
              <CheckPermission permission={Permissions.visualizarProntuario.soap.plano.visualizarReceitas}>
                <TabLink to={`${match.url}/prescricao-medicamentos`}>Prescrição de medicamentos</TabLink>
              </CheckPermission>
              <CheckPermission permission={Permissions.visualizarProntuario.soap.plano.visualizarAtestados}>
                <TabLink to={`${match.url}/atestados`}>Atestados</TabLink>
              </CheckPermission>
              {hasAutorizationDeclaracaoTab && (
                <TabLink to={`${match.url}/declaracaoComparecimento/`}>Declaração de comparecimento</TabLink>
              )}
            </Tabs>
          </Cell>
        </Grid>
        <PlanoRootView
          name={name}
          prontuarioId={prontuarioId}
          atendimentoId={atendimentoId}
          cidadao={cidadao}
          dataAtendimento={dataAtendimento}
          cidadaoDataNascimento={cidadao.dataNascimento}
          cidadaoSexo={cidadao.sexo}
        />
      </VFlow>
    </>
  )
}
